import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActionArea,
  Stack, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Menu, MenuItem, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { botsApi } from "../../api/bots.js";
import { BadgeChip, botStateToBadgeColor, botStateLabel, statusToBadgeColor } from "../../components/BadgeChip.js";
import { BotLetterMark } from "../../components/BotLetterMark.js";
import { GlowButton } from "../../components/GlowButton.js";
import { EmptyState } from "../../components/EmptyState.js";
import type { Bot } from "@chatfab/shared";

export function BotsPage() {
  const nav = useNavigate();
  const [bots, setBots]         = useState<Bot[]>([]);
  const [loading, setLoading]   = useState(true);
  const [createOpen, setCreate] = useState(false);
  const [search, setSearch]     = useState("");

  const load = () => {
    setLoading(true);
    botsApi.list().then(setBots).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = search.trim()
    ? bots.filter(b => b.name.toLowerCase().includes(search.toLowerCase()) || b.slug.toLowerCase().includes(search.toLowerCase()))
    : bots;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h2">Your Bots</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Create and manage your AI chatbots.
          </Typography>
        </Box>
        <GlowButton startIcon={<AddIcon />} onClick={() => setCreate(true)}>
          New Bot
        </GlowButton>
      </Stack>

      {bots.length > 0 && (
        <TextField
          size="small" placeholder="Search bots…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ mb: 2, width: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[1,2,3].map(i => <Grid item xs={12} sm={6} md={4} key={i}><Skeleton variant="rounded" height={160} /></Grid>)}
        </Grid>
      ) : bots.length === 0 ? (
        <EmptyState
          icon={<SmartToyOutlinedIcon />}
          title="No bots yet"
          description="Create your first bot and upload documents to get started."
          action={
            <GlowButton startIcon={<AddIcon />} onClick={() => setCreate(true)}>
              Create your first bot
            </GlowButton>
          }
        />
      ) : (
        <Grid container spacing={2}>
          {filtered.length === 0 ? (
            <Grid item xs={12}>
              <Typography color="text.secondary">No bots match your search.</Typography>
            </Grid>
          ) : (
            filtered.map(bot => <Grid item xs={12} sm={6} md={4} key={bot.bot_id}><BotCard bot={bot} onRefresh={load} /></Grid>)
          )}
        </Grid>
      )}

      <CreateBotDialog open={createOpen} onClose={() => setCreate(false)} onCreated={bot => { setCreate(false); nav(`/dashboard/bots/${bot.bot_id}`); }} />
    </Box>
  );
}

function BotCard({ bot, onRefresh }: { bot: Bot; onRefresh: () => void }) {
  const nav = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const handleState = async (state: string) => {
    setAnchor(null);
    await botsApi.setState(bot.bot_id, state);
    onRefresh();
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardActionArea onClick={() => nav(`/dashboard/bots/${bot.bot_id}`)} sx={{ flex: 1, alignItems: "stretch", display: "flex", flexDirection: "column" }}>
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
            <BotLetterMark name={bot.name} size={40} />
            <BadgeChip label={botStateLabel(bot.state)} color={botStateToBadgeColor(bot.state)} />
          </Stack>
          <Typography variant="h5" noWrap>{bot.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {bot.description || "No description"}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Box><Typography variant="caption">Sources</Typography><Typography variant="h5">{Math.max(0, bot.document_count ?? 0)}</Typography></Box>
            <Box>
              <Typography variant="caption">Status</Typography>
              <Box mt={0.25}><BadgeChip label={bot.processing_status} color={statusToBadgeColor(bot.processing_status)} /></Box>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>

      <Box sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {bot.state === "active"  && <MenuItem onClick={() => handleState("paused")}>Pause</MenuItem>}
        {bot.state === "paused"  && <MenuItem onClick={() => handleState("active")}>Activate</MenuItem>}
        {bot.state === "deleted" && <MenuItem onClick={async () => { await botsApi.restore(bot.bot_id); setAnchor(null); onRefresh(); }}>Restore</MenuItem>}
        <Divider />
        <MenuItem onClick={async () => { await botsApi.delete(bot.bot_id); setAnchor(null); onRefresh(); }} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
}

function CreateBotDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (bot: Bot) => void }) {
  const [name, setName]       = useState("");
  const [desc, setDesc]       = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const bot = await botsApi.create({ name, description: desc });
      onCreated(bot);
      setName(""); setDesc("");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Create a new bot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Bot name" fullWidth required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Support Assistant" />
            <TextField label="Description" fullWidth value={desc} onChange={e => setDesc(e.target.value)} placeholder="What does this bot help with?" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Cancel</Button>
          <GlowButton type="submit" loading={loading}>
            {loading ? "Creating…" : "Create Bot"}
          </GlowButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
